"use client";

import EditSection, { TextEditor, ListItemEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";
import { PiUploadSimple, PiX } from "react-icons/pi";
import logger from "@/utils/logger";

export default function HokFaqEdit({ content, onContentChange }: SectionContentProps) {
  const handleChange = (updated: Record<string, any>) => {
    onContentChange?.(updated);
  };

  return (
    <EditSection
      content={content}
      onContentChange={handleChange}
      contentFields={
        <>
          {/* Section Heading */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Section Heading</h4>
            <TextEditor
              label="Subtitle (above title)"
              value={content.faqSubtitle}
              onChange={(v) => handleChange({ ...content, faqSubtitle: v })}
              placeholder="FAQ"
            />
            <TextEditor
              label="Title Line 1"
              value={content.faqTitleLine1}
              onChange={(v) => handleChange({ ...content, faqTitleLine1: v })}
              placeholder="Unlock Your True Potential"
            />
            <TextEditor
              label="Title Line 2"
              value={content.faqTitleLine2}
              onChange={(v) => handleChange({ ...content, faqTitleLine2: v })}
              placeholder="With Our Guidance"
            />
          </div>

          {/* Side Image */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Side Image</h4>
            
            {/* Show image preview */}
            {content.faqImage && (
              <div className="mb-2 relative group">
                <div className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200">
                  <img
                    src={content.faqImage}
                    alt="Preview"
                    className="w-12 h-12 object-cover rounded border shadow-sm"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-gray-700 truncate">
                      FAQ Image Preview
                    </p>
                    <p className="text-[9px] text-gray-400 truncate">
                      {content.faqImage.startsWith("data:") ? "Base64 Image" : content.faqImage}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange({ ...content, faqImage: "" })}
                    className="p-1 rounded bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                    title="Remove image"
                  >
                    <PiX className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={content.faqImage || ""}
                onChange={(e) => handleChange({ ...content, faqImage: e.target.value })}
                placeholder="Image URL (e.g. https://...)"
                className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>

            <div className="flex items-center gap-2 py-1">
              <div className="h-px bg-gray-200 flex-1"></div>
              <span className="text-[9px] text-gray-400 font-bold uppercase">OR</span>
              <div className="h-px bg-gray-200 flex-1"></div>
            </div>

            <label className="flex items-center justify-center gap-1.5 w-full p-2.5 border border-dashed border-gray-300 rounded hover:border-purple-500 hover:bg-purple-50/50 cursor-pointer transition-all bg-white group active:scale-[0.98]">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error("Image size must be less than 5MB");
                      return;
                    }

                    const uploadPromise = homepageApi.uploadImage(file);

                    toast.promise(uploadPromise, {
                      loading: "Uploading image...",
                      success: "Image uploaded!",
                      error: "Failed to upload image",
                    });

                    uploadPromise
                      .then((response) => {
                        if (response.success && response.data?.url) {
                          handleChange({ ...content, faqImage: response.data.url });
                        } else {
                          toast.error(response.message || "Upload failed");
                        }
                      })
                      .catch((err) => {
                        logger.error(err);
                        toast.error("Upload failed");
                      });
                  }
                }}
              />
              <PiUploadSimple className="h-4 w-4 text-gray-400 group-hover:text-purple-500" />
              <span className="text-[11px] font-semibold text-gray-600 group-hover:text-purple-600">
                Upload from Device
              </span>
            </label>
          </div>

          {/* CTA Card */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Floating CTA Card</h4>
            <TextEditor
              label="CTA Title"
              value={content.faqCtaTitle}
              onChange={(v) => handleChange({ ...content, faqCtaTitle: v })}
              placeholder="Still Have Questions?"
            />
            <TextEditor
              label="CTA Subtitle"
              value={content.faqCtaSubtitle}
              onChange={(v) => handleChange({ ...content, faqCtaSubtitle: v })}
              placeholder="Short description..."
              multiline
            />
            <TextEditor
              label="Button Label"
              value={content.faqCtaButton}
              onChange={(v) => handleChange({ ...content, faqCtaButton: v })}
              placeholder="Get In Touch"
            />
            <TextEditor
              label="Button URL"
              value={content.faqCtaUrl}
              onChange={(v) => handleChange({ ...content, faqCtaUrl: v })}
              placeholder="#contact"
            />
          </div>

          {/* FAQ Items */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">FAQ Items</h4>
            <ListItemEditor
              label="Questions"
              items={content.faqs || [
                { q: "How Much Does It Cost?", a: "We offer flexible plans starting from free." },
                { q: "What Is Included In The Price?", a: "All plans include core features." },
              ]}
              itemSchema={[
                { key: "q", label: "Question", type: "text" },
                { key: "a", label: "Answer", type: "text" },
              ]}
              onChange={(items) => handleChange({ ...content, faqs: items })}
              addItemText="Add FAQ Item"
            />
          </div>
        </>
      }
    />
  );
}
